/* eslint-disable no-unreachable */
import { ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import { Button, notification } from 'antd';
import findIndex from 'lodash/findIndex';
import forEach from 'lodash/forEach';
import range from 'lodash/range';
import React, { useEffect, useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import { useDispatch } from 'react-redux';
import { ReactSVG } from 'react-svg';
import { v4 as uuidv4 } from 'uuid';
import { STEP_TYPE } from '../../../constants';
import {
  doLockRiderInsideRoutePlan,
  doOptimizeRoutePlan,
  doReorderRoutePlan,
} from '../../../modules/route-plans/stores/thunk';
import { getFirstStartAndLastEnd, getRiderIds } from '../../Mapbox/Utils';
import DriverList from './DriverList';
import DriverTime from './DriverTime';
import './RoutePlanChart.scss';
import StopsMapbox from './StopsMapBox';
import UnScheduled from './UnScheduled';

const RoutePlanChart = ({
  routePlan,
  setIsOptimize,
  stops,
  mapData,
  oneHourPx,
  handleZoomIn,
  handleZoomOut,
  setClicked,
  clicked,
  stepsHovered,
  solutions,
  setSolutions,
  setStepsHovered,
  onClickStop,
  setShowMoreRoute,
  showMoreRoute,
  onClosePopup,
  onClickRoute,
  routeType,
  setRouteType,
  routeClicked,
  stepClicked,
  riderIdsHide,
  setRiderIdsHide,
  orders,
  riderIdsLock,
  setRiderIdsLock,
  stepCompleted,
  setRoutesChange,
  routesChange,
  onClickSwap,
}) => {
  const dispatch = useDispatch();
  const [api, contextHolder] = notification.useNotification();
  const [hourStart, setHourStart] = useState(8);
  const [hourEnd, setHourEnd] = useState(8);
  const [startTime, setStartTime] = useState(8);
  const [isDragging, setIsDragging] = useState(false);

  const routes = routePlan?.riders;

  const onDragStart = () => {
    setIsDragging(true);
  };

  const handleOptimizeForce = () => {
    dispatch(
      doOptimizeRoutePlan({
        id: routePlan.slug,
        forceAll: true,
      }),
    );
    onClosePopup();
  };

  useEffect(() => {
    if (routes?.length) {
      const [startAt, endAt] = getFirstStartAndLastEnd(routes);
      const startHour = startAt && startAt?.split(':')[0];
      setHourStart(Number(startHour));
      const endHour = endAt && endAt?.split(':')[0];
      setHourEnd(Number(endHour));
      const startHourFull = `${startHour}:00`;
      setStartTime(startHourFull);
    }
  }, [routes, stops]);

  const hours = range(hourStart, hourEnd + 1);
  const minutes = range(0, (hourEnd - hourStart) * 2 + 2);

  const onHoverStep = (step) => {
    setClicked(false);
    onClickStop(step);
  };

  const onClickStep = (step) => {
    setClicked(true);
    onClickStop(step);
  };

  const handleCloseStepPopup = () => {
    if (!clicked) {
      onClosePopup();
    }
  };

  const handleOnClickRoute = (route, type) => {
    setClicked(true);
    setRouteType(type);
    onClickRoute(route);
  };

  const onClickHide = (riderId) => {
    const riderIds = getRiderIds(routes, riderIdsHide, riderId);
    setRiderIdsHide(riderIds);
    onClickStop({ id: 'closed' });
  };

  const onClickLock = (riderId) => {
    const riderIds = getRiderIds(routes, riderIdsLock, riderId);
    setRiderIdsLock(riderIds);
    dispatch(
      doLockRiderInsideRoutePlan({
        id: routePlan.slug,
        riderId: riderId !== 'all-riders' ? riderId : undefined,
        locked: riderIds.includes(riderId),
      }),
    );
  };

  const handleReorderRoutePlan = async (newSolutions, paramsRoutesChange = []) => {
    const routes = [];
    let unscheduled;
    let isHasUnassign = false;
    const slug = routePlan.slug;
    const realRoutes = routePlan.riders;

    Object.keys(newSolutions).forEach((key) => {
      if (isHasUnassign) return;
      if (key.includes('assign_')) {
        if (newSolutions[key].steps?.length > 0) {
          isHasUnassign = true;
          return;
        }
        return;
      } else if (key === 'unscheduled') {
        if (!paramsRoutesChange.includes(key)) return;
        const steps = newSolutions[key].steps || [];
        const stepsFilter = steps.filter((s) => s.type === STEP_TYPE.delivery);
        unscheduled = stepsFilter.map((sf) => String(sf.orderId));
      } else {
        if (!paramsRoutesChange.includes(key) && !paramsRoutesChange.includes('assign_' + key)) return;
        if (newSolutions['assign_' + key].steps?.length > 0) {
          isHasUnassign = true;
          return;
        }
        const stepsParam = [];
        const steps = newSolutions[key].steps || [];
        const realRoute = realRoutes.find((r) => r.riderId == key);
        steps.forEach((step) => {
          if (step.type === STEP_TYPE.pickup) {
            step.orderIds.forEach((orderId) => {
              stepsParam.push(orderId + '_' + step.type);
            });
          } else {
            stepsParam.push(step.orderId + '_' + step.type);
          }
        });
        routes.push({ rider_id: Number(realRoute.id), steps: stepsParam });
      }
    });
    if (isHasUnassign || routes.length === 0) return;
    await dispatch(
      doReorderRoutePlan({
        id: slug,
        routes,
        unscheduled,
      }),
    );
    setIsOptimize(true);
    setRoutesChange([]);
  };

  const onDragEnd = (result) => {
    setIsDragging(false);
    const { source, destination } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId) {
      if (destination.index === source.index) return;
      const { steps, color } = solutions[source.droppableId];
      const sourceClone = [...steps];
      const stop = sourceClone[source.index];
      const type = stop.type === STEP_TYPE.pickup ? STEP_TYPE.delivery : STEP_TYPE.pickup;
      if (stop.type === STEP_TYPE.delivery) {
        const stopPickupIndex = findIndex(sourceClone, (s) => s.orderIds?.includes(stop.orderId) && s.type === type);
        if (destination.index <= stopPickupIndex) {
          api.error({
            message: 'Error',
            description:
              'Preventing the relocation of the drop-off point ahead of the pickup point is a required restriction.',
            style: { top: '52px' },
          });
          return;
        }
      } else {
        let isFalse = false;
        const orderIds = stop.orderIds;
        forEach(orderIds, (orderId) => {
          if (isFalse) return;
          const stopOrderIndex = findIndex(sourceClone, (s) => s.orderId === orderId && s.type === type);
          isFalse = destination.index >= stopOrderIndex;
        });
        if (isFalse) {
          api.error({
            message: 'Error',
            description:
              'Preventing the relocation of the drop-off point ahead of the pickup point is a required restriction.',
            style: { top: '52px' },
          });
          return;
        }
      }
      const stopLeftIndex =
        type === STEP_TYPE.delivery
          ? findIndex(sourceClone, (s) => s.orderId === stop.orderId && s.type === type)
          : findIndex(
              sourceClone,
              (s) => JSON.stringify(s.orderIds) === JSON.stringify(stop.orderIds) && s.type === type,
            );
      if (
        (type === STEP_TYPE.delivery && destination.index >= stopLeftIndex) ||
        (type === STEP_TYPE.pickup && destination.index <= stopLeftIndex)
      )
        return;
      const [removed] = sourceClone.splice(source.index, 1);
      sourceClone.splice(destination.index, 0, {
        ...removed,
        duration: 20,
        marginLeft: 8,
      });
      const newSolutions = { ...solutions, [source.droppableId]: { steps: sourceClone, color } };
      const newRoutesChange = [...routesChange, source.droppableId];
      setRoutesChange(newRoutesChange);
      setSolutions(newSolutions, newRoutesChange);
      handleReorderRoutePlan(newSolutions, newRoutesChange);
      if (destination.droppableId.includes('assign') || destination.droppableId === 'unscheduled') return;
    } else {
      moveStep(source, destination);
    }
  };

  const moveStep = (droppableSource, droppableDestination) => {
    const stepsSource = solutions[droppableSource.droppableId].steps;
    const colorSource = solutions[droppableSource.droppableId].color;
    const stepsDestination = solutions[droppableDestination.droppableId].steps;
    const colorDestination = solutions[droppableDestination.droppableId].color;
    const sourceClone = [...stepsSource];
    const destClone = [...stepsDestination];
    const stop = sourceClone[droppableSource.index];
    const type = stop.type === STEP_TYPE.pickup ? STEP_TYPE.delivery : STEP_TYPE.pickup;
    const newSolutions = { ...solutions };
    if (stop.type === STEP_TYPE.pickup) {
      const orderIds = stop.orderIds;
      const [removed] = sourceClone.splice(droppableSource.index, 1);
      const destPickupIndex = destClone.findIndex(
        (dest) => dest.lat === removed.lat && dest.lng === removed.lng && dest.locationId === removed.locationId,
      );
      const destPickup = destPickupIndex === -1 ? null : destClone[destPickupIndex];
      if (destPickup) {
        destClone.splice(destPickupIndex, 1, {
          ...destPickup,
          // duration: 10,
          orderIds: [...destPickup.orderIds, ...orderIds],
        });
      } else {
        destClone.splice(droppableDestination.index, 0, {
          ...removed,
          id: uuidv4(),
          orderIds: [...orderIds],
          marginLeft: 8,
          // width: '20px',
        });
      }
      if (orderIds.length > 0) {
        orderIds.forEach((orderId) => {
          const stopLeftIndex = findIndex(sourceClone, (s) => s.orderId === orderId && s.type === type);
          if (stopLeftIndex > -1) {
            const destinationIndex = destPickup ? droppableDestination.index : droppableDestination.index + 1;
            const [removedLeft] = sourceClone.splice(stopLeftIndex, 1);
            destClone.splice(destinationIndex, 0, {
              ...removedLeft,
              id: uuidv4(),
              marginLeft: 8,
            });
          }
        });
      }
    } else {
      const [removed] = sourceClone.splice(droppableSource.index, 1);
      destClone.splice(droppableDestination.index, 0, {
        ...removed,
        // duration: 10,
        id: uuidv4(),
        marginLeft: 8,
      });
      const stopPickupIndex = findIndex(sourceClone, (s) => s.orderIds?.includes(stop.orderId) && s.type === type);
      const stopPickup = sourceClone[stopPickupIndex];
      const orderIds = stopPickup.orderIds;
      if (orderIds?.length === 1) {
        const destinationIndex = droppableDestination.index;
        const [removedLeft] = sourceClone.splice(stopPickupIndex, 1);
        const destPickupIndex = destClone.findIndex(
          (dest) =>
            dest.lat === removedLeft.lat && dest.lng === removedLeft.lng && dest.locationId === removedLeft.locationId,
        );
        if (destPickupIndex === -1) {
          destClone.splice(destinationIndex, 0, {
            ...removedLeft,
            id: uuidv4(),
            marginLeft: 8,
            orderIds: [stop.orderId],
            // width: '20px',
          });
        } else {
          const destPickup = destClone[destPickupIndex];
          destClone.splice(destPickupIndex, 1, {
            ...destPickup,
            orderIds: [...destPickup.orderIds, stop.orderId],
          });
        }
      } else if (orderIds?.length > 1) {
        const destinationIndex =
          type === STEP_TYPE.pickup ? droppableDestination.index : droppableDestination.index + 1;
        const orderIdIndex = findIndex(orderIds, (id) => id === stop.orderId);
        orderIds.splice(orderIdIndex, 1);
        sourceClone.splice(stopPickupIndex, 1, {
          ...stopPickup,
          orderIds,
          id: uuidv4(),
          marginLeft: 8,
          // width: '20px',
        });
        // const [removedLeft] = sourceClone.splice(stopPickupIndex, 1);
        const destPickupIndex = destClone.findIndex(
          (dest) =>
            dest.lat === stopPickup.lat && dest.lng === stopPickup.lng && dest.locationId === stopPickup.locationId,
        );
        if (destPickupIndex === -1) {
          destClone.splice(destinationIndex, 0, {
            ...stopPickup,
            orderIds: [stop.orderId],
            // duration: 10,
            marginLeft: 8,
            id: uuidv4(),
            // width: '20px',
          });
        } else {
          const destPickup = destClone[destPickupIndex];
          destClone.splice(destPickupIndex, 1, {
            ...destPickup,
            // duration: 10,
            orderIds: [...destPickup.orderIds, stop.orderId],
          });
        }
      }
    }
    newSolutions[droppableSource.droppableId] = { steps: sourceClone, color: colorSource };
    newSolutions[droppableDestination.droppableId] = { steps: destClone, color: colorDestination };
    const newRoutesChange = [...routesChange, droppableSource.droppableId, droppableDestination.droppableId];
    setRoutesChange(newRoutesChange);
    setSolutions(newSolutions);
    handleReorderRoutePlan(newSolutions, newRoutesChange);
  };

  return (
    <div className="ContentMain RoutePlanChart">
      {contextHolder}
      <div className="wrapper-map">
        <StopsMapbox
          mapData={mapData}
          onClickStop={onClickStop}
          stepClicked={stepClicked}
          routeClicked={routeClicked}
          onClickRoute={onClickRoute}
          setRouteType={setRouteType}
          onClosePopup={onClosePopup}
          routePlan={routePlan}
          clicked={clicked}
          showMoreRoute={showMoreRoute}
          routeType={routeType}
          setShowMoreRoute={setShowMoreRoute}
          orders={orders}
          riderIdsHide={riderIdsHide}
        />
      </div>
      <div className="content">
        <div className="header">
          <div className="left"></div>
          <div className="right">
            <Button onClick={handleZoomOut}>
              <ZoomOutOutlined />
            </Button>
            <Button onClick={handleZoomIn}>
              <ZoomInOutlined />
            </Button>
            <Button size="small" className="btn-force" onClick={handleOptimizeForce}>
              Force Schedule All
              <ReactSVG className="icon" src="/icons/force.svg" />
            </Button>
          </div>
        </div>
        <div className="ChartContent">
          <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
            <div className="tbody unscheduled" style={{ width: '100%', height: '48px' }}>
              <UnScheduled
                steps={solutions['unscheduled']?.steps?.filter((s) => s.type != STEP_TYPE.pickup) || []}
                realSteps={solutions['unscheduled']?.steps || []}
                stepsHovered={stepsHovered}
                setStepsHovered={setStepsHovered}
                onClickStep={onClickStep}
                onHoverStep={onHoverStep}
                handleCloseStepPopup={handleCloseStepPopup}
                isDragging={isDragging}
                riderIdsHide={riderIdsHide}
                onClickHide={onClickHide}
              />
            </div>
            <div className="scrollCustom tbody">
              <DriverTime
                hours={hours}
                oneHourPx={oneHourPx}
                onClickHide={onClickHide}
                riderIdsHide={riderIdsHide}
                onClickLock={onClickLock}
                riderIdsLock={riderIdsLock}
              />
              <DriverList
                routePlan={routePlan}
                isDragging={isDragging}
                riderIdsHide={riderIdsHide}
                hours={hours}
                oneHourPx={oneHourPx}
                minutes={minutes}
                startTime={startTime}
                solutions={solutions}
                stepCompleted={stepCompleted}
                onHoverStep={onHoverStep}
                stepsHovered={stepsHovered}
                setStepsHovered={setStepsHovered}
                handleCloseStepPopup={handleCloseStepPopup}
                onClickStep={onClickStep}
                onClickRoute={handleOnClickRoute}
                setShowMoreRoute={setShowMoreRoute}
                onClickHide={onClickHide}
                onClickLock={onClickLock}
                riderIdsLock={riderIdsLock}
                onClickSwap={onClickSwap}
              />
            </div>
          </DragDropContext>
        </div>
      </div>
    </div>
  );
};

export default RoutePlanChart;
